import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BreakingTicker from "@/components/feature/BreakingTicker";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { articles, channels } from "@/mocks/news";
import { formatHeat } from "@/utils/format";

type SortKey = "latest" | "hot";

export default function Dispatch() {
  const { slug = "" } = useParams();
  const channel = channels.find((c) => c.slug === slug);
  const [sort, setSort] = useState<SortKey>("latest");
  const [expanded, setExpanded] = useState(false);

  const channelName = channel?.name ?? "突发专报";

  const list = useMemo(() => {
    const base = articles.filter((a) => a.channel === slug);
    const sorted = [...base].sort((a, b) =>
      sort === "hot" ? b.heat - a.heat : b.publishedAt.localeCompare(a.publishedAt)
    );
    return sorted;
  }, [slug, sort]);

  usePageMeta({
    title: `${channelName} - 最新动态与脉络追踪`,
    description: `瓜田速递${channelName}频道，聚合最新${channelName}内容，提供可核对的时间线、官方回应与脉络梳理，帮助你在吃瓜前线快人一步。`,
    keywords: `瓜田速递,${channelName},突发专报,紧急插播,重大瓜情`,
    path: `/dispatch/${slug}`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${channelName} - 瓜田速递`,
        url: `https://cllcr.cn/dispatch/${slug}`,
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.title,
            url: `https://cllcr.cn/news/${item.id}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://cllcr.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: channelName,
            item: `https://cllcr.cn/dispatch/${slug}`,
          },
        ],
      },
    ],
  });

  const visible = expanded ? list : list.slice(0, 6);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <BreakingTicker />
      <main className="flex-1">
        <section className="w-full bg-foreground-950">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <div className="[&_a]:text-background-200/70 [&_span]:text-background-100">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: channelName }]} />
            </div>
            <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-label font-medium">
                  <i className={channel?.icon ?? "ri-flashlight-fill"} aria-hidden="true"></i>
                  {channelName}
                </span>
                <h1 className="mt-4 font-heading text-3xl md:text-4xl font-black text-background-50">
                  {channelName}频道
                </h1>
                <p className="mt-3 text-sm md:text-base text-background-200/80 leading-relaxed">
                  {channel?.desc ?? "正在发生的动态，第一时间直给。"}
                </p>
              </div>
              <dl className="flex items-center gap-6">
                <div>
                  <dt className="text-xs font-label text-background-200/60">当前收录</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-accent-400">{list.length}</dd>
                </div>
                <div className="w-px h-10 bg-background-100/15"></div>
                <div>
                  <dt className="text-xs font-label text-background-200/60">最高热度</dt>
                  <dd className="mt-1 font-heading text-2xl font-black text-accent-400">
                    {list.length ? formatHeat(Math.max(...list.map((a) => a.heat))) : "0"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl font-black text-foreground-950">
                全部{channelName}
                <span className="ml-2 text-sm font-body font-normal text-foreground-500">
                  共 {list.length} 条
                </span>
              </h2>
              <div className="inline-flex items-center rounded-full bg-background-100 border border-background-200 px-1 py-1">
                {(
                  [
                    { key: "latest", label: "最新优先", icon: "ri-time-line" },
                    { key: "hot", label: "热度优先", icon: "ri-fire-line" },
                  ] as const
                ).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                      sort === option.key
                        ? "bg-foreground-900 text-background-50"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <i className={option.icon} aria-hidden="true"></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {visible.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {visible.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <i className="ri-inbox-archive-line text-4xl text-foreground-300" aria-hidden="true"></i>
                <p className="mt-4 text-sm text-foreground-500">
                  该频道暂无收录内容，可先浏览其他频道。
                </p>
                <Link
                  to="/"
                  className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}

            {list.length > 6 && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 bg-background-50 text-sm font-label text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {expanded ? "收起列表" : `加载更多（其余 ${list.length - 6} 条）`}
                  <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
                </button>
              </div>
            )}
          </div>
        </section>

        <section aria-label="其他频道" className="w-full bg-background-100/70 border-t border-background-200">
          <div className="w-full px-4 md:px-8 py-12">
            <h2 className="font-heading text-lg font-black text-foreground-950 mb-6">换个频道继续吃瓜</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {channels
                .filter((c) => c.slug !== slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/dispatch/${c.slug}`}
                    className="group flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <span className="flex-1">
                      <span className="block font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {c.name}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500 leading-relaxed line-clamp-2">
                        {c.desc}
                      </span>
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}